import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ensureToken } from '../../redux/thunk/djpalThunk';
import { resolveMixCover } from '../../utils/nativeMixCover';
import { playAllTracks } from '../../utils/playAllTracks';
import { entityId } from '../../utils/entityId';
import { useToast } from '../../contexts/ToastContext';
import { isSamplesOnly } from '../../utils/webOnly';
import { useMixPlayback, canPlayMix } from './MixPlayer';
import {
    homeRowCache,
    homeRowCacheKey,
    cacheIsFresh,
    loadHomeRow,
    loadHomeRowMore,
    rowHasMore,
    goMix,
    goMixesPlayingTrack,
    goTrack,
} from './mixpediaHomeData';
import CoverArt from '../CoverArt';
import MixpediaBrowseList from './MixpediaBrowseList';
import ViewToggle, { useViewMode } from '../common/ViewToggle';
import { useDesignV2 } from '../../utils/designV2';
import './MixpediaHome.scss';

// Full-page view for a Mixpedia home row ("See all"). kind:
//   'sets'   — all new mixes from followed artists
//   'tracks' — all tracks recently played by followed artists
// Wrapped grid + Load more (server-paged). Shares the home's session cache.
const TITLES = {
    sets: 'New mixes from artists you follow',
    tracks: 'Tracks played recently by artists you follow',
    artistTracks: 'Tracks by artists you follow, played by other DJs',
};

const VIEW_PAGE = { sets: 'mixpedia_sets', tracks: 'mixpedia_tracks', artistTracks: 'mixpedia_artist_tracks' };

const MixpediaBrowsePage = ({ kind }) => {
    const dispatch = useDispatch();
    const store = useStore();
    // DESIGN V2: the For You list (play, waveform, actions) by default, the
    // cover grid one click away; remembered per row on this device.
    const v2 = useDesignV2();
    const [viewMode, setViewMode] = useViewMode(VIEW_PAGE[kind], 'list');
    const asList = v2 && viewMode === 'list';
    const followed = useSelector((state) => state.followedArtists.artists);
    const userId = useSelector((state) => state.auth.user?.id);
    const { playMix, loadingMixId, playingMixId } = useMixPlayback();
    const toast = useToast();
    const isPlaying = useSelector((state) => state.musicPlayer.isPlaying);
    // The playing-track identity comes from the queue context we set when
    // starting playback (musicPlayer.currentTrack's name is a combined
    // "Title - Artist" string — not reliable for matching row items).
    const listItems = useSelector((state) => state.listContext.items);
    const listIndex = useSelector((state) => state.listContext.currentIndex);
    const nowItem = listIndex >= 0 ? listItems?.[listIndex] : null;
    // Samples-only platforms (web, iOS, Android) can't full-play YT/SC links
    // — a play button gated on those would lie there.
    const trackPlayable = (t) => isSamplesOnly
        ? !!(t.sampleUrl || t.previewUrl || t.preview_url || t.preview)
        : !!(t.sampleUrl || t.previewUrl || t.preview_url || t.preview || t.youtubeId || t.soundcloudUrl);
    const pausePlayback = (e) => {
        e.stopPropagation();
        const a = document.querySelector('audio');
        if (a && !a.paused) a.pause();
    };
    const isSameAsNow = (t) => {
        if (!nowItem) return false;
        const idA = entityId(nowItem);
        const idB = entityId(t);
        if (idA && idB) return idA === idB;
        return nowItem.title === t.title && (nowItem.artist || '') === (t.artist || '');
    };
    const isTrackPlaying = (t) => isPlaying && isSameAsNow(t);
    // Clicking play on the loaded-but-paused item resumes in place instead of
    // re-resolving and restarting from 0:00.
    const resumeIfCurrent = (matches) => {
        if (!matches) return false;
        const a = document.querySelector('audio');
        if (a && a.paused) { a.play().catch(() => {}); return true; }
        return false;
    };

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [resolvingTrackIdx, setResolvingTrackIdx] = useState(null);
    const sentinelRef = useRef(null);
    const fetchedRef = useRef(false);

    // Send the whole follow list (defensive cap only) — a hard 50-cap made
    // big follow sets feel arbitrarily truncated. Server cost is bounded by
    // a recent-window query shape above ~100 follows.
    const followedNames = (followed || []).map(a => a.artist_name).filter(Boolean).slice(0, 500);
    const cacheKey = homeRowCacheKey(userId, followedNames);

    useEffect(() => {
        if (fetchedRef.current || !followedNames.length) return;
        fetchedRef.current = true;
        if (cacheIsFresh(kind, cacheKey)) {
            setItems(homeRowCache[kind].items);
            setHasMore(rowHasMore(homeRowCache[kind]));
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const token = await ensureToken(dispatch, store.getState);
                const cache = await loadHomeRow(kind, token, followedNames, cacheKey);
                if (cancelled) return;
                setItems(cache.items);
                setHasMore(rowHasMore(cache));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, cacheKey, dispatch, store]);

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const token = await ensureToken(dispatch, store.getState);
            const cache = await loadHomeRowMore(kind, token, followedNames, cacheKey);
            setItems(cache.items);
            setHasMore(rowHasMore(cache));
        } finally {
            setLoadingMore(false);
        }
    };

    // Infinite scroll: auto-load the next page when the sentinel below the
    // grid comes into view (same pattern as TrackListSearch).
    useEffect(() => {
        if (!hasMore) return undefined;
        const el = sentinelRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !loadingMore) loadMore();
        // ~3 grid rows of lead: the next page's DATA + its cover warm
        // (loadHomeRowMore) must both land before the user scrolls onto the
        // new tiles, or the covers visibly trail on slow connections. 400px
        // (~1 page) lost that race at any real scrolling pace.
        }, { rootMargin: '1200px' });
        observer.observe(el);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loadingMore, items.length]);


    return (
        <div className="mixpedia-home mixpedia-browse-page">
            <div className="mh-section-header">
                <h3 className="mh-title">{TITLES[kind]}</h3>
                {v2 && <ViewToggle mode={viewMode} onChange={setViewMode} gridTitle="Covers" listTitle="List with waveforms" />}
            </div>

            {loading && items.length === 0 && <div className="mh-loading">Loading…</div>}

            {asList ? <MixpediaBrowseList kind={kind} items={items} /> : (
            <div className="mh-browse-grid">
                {items.map((item, i) => (
                    kind === 'sets' ? (
                        <div key={`${item.id || item.title}-${i}`} className="mh-mix-card" onClick={() => goMix(dispatch, item, 'browse')} role="button" tabIndex={0}>
                            <div className="mh-mix-art-wrap">
                                {resolveMixCover(item) || item.artworkUrl ? (
                                    <CoverArt src={resolveMixCover(item) || item.artworkUrl} size={130} className="mh-mix-art" />
                                ) : (
                                    <span className="mh-mix-art is-empty">&#9835;</span>
                                )}
                                {playingMixId === item.id && isPlaying ? (
                                    <button className="mh-hover-play playing" title="Pause" onClick={pausePlayback}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                    </button>
                                ) : (!isSamplesOnly && canPlayMix(item) && item.link && (
                                    <button
                                        className="mh-hover-play"
                                        title="Play"
                                        onClick={(e) => { e.stopPropagation(); if (resumeIfCurrent(playingMixId === item.id)) return; playMix(item, { items, index: i }); }}
                                    >
                                        {loadingMixId === item.id
                                            ? <span className="mix-play-spinner" />
                                            : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                                    </button>
                                ))}
                            </div>
                            <span className="mh-mix-title" title={item.title || item.name}>{item.title || item.name}</span>
                            <span className="mh-mix-sub">
                                {(item.djName || item.artist || '')}{item.date ? ` · ${String(item.date).slice(0, 10)}` : ''}
                            </span>
                        </div>
                    ) : (
                        <div key={`${item.id || item.title}-${i}`} className="mh-mix-card" onClick={() => goTrack(dispatch, item, 'browse')} role="button" tabIndex={0}>
                            <div className="mh-mix-art-wrap">
                                {item.artworkUrl ? (
                                    <CoverArt src={item.artworkUrl} size={130} className="mh-mix-art" />
                                ) : (
                                    <span className="mh-mix-art is-empty">&#9835;</span>
                                )}
                                {isTrackPlaying(item) ? (
                                    <button className="mh-hover-play playing" title="Pause" onClick={pausePlayback}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                    </button>
                                ) : (trackPlayable(item) && (
                                    <button
                                        className="mh-hover-play"
                                        title="Play"
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            if (resumeIfCurrent(isSameAsNow(item))) return;
                                            if (resolvingTrackIdx !== null) return;
                                            setResolvingTrackIdx(i);
                                            try {
                                                const ok = await playAllTracks(dispatch, items, i);
                                                if (!ok) toast.info("Couldn't find a playable source for this track");
                                            }
                                            finally { setResolvingTrackIdx(null); }
                                        }}
                                    >
                                        {resolvingTrackIdx === i
                                            ? <span className="mix-play-spinner" />
                                            : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                                    </button>
                                ))}
                            </div>
                            <span className="mh-mix-title" title={item.title}>{item.title}</span>
                            <span className="mh-mix-sub">{item.artist || ''}</span>
                            {item.recentPlays > 0 && (
                                <span
                                    className="mh-sub-badge mh-sub-badge-link"
                                    title="See the sets that played this"
                                    onClick={(e) => { e.stopPropagation(); goMixesPlayingTrack(dispatch, item, homeRowCache.artistTracks.meta?.windowDays || 90); }}
                                >
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                                    <span className="mh-sub-badge-name">{item.recentPlays} {item.recentPlays === 1 ? 'play' : 'plays'}</span>
                                </span>
                            )}
                            {item.mixDj && (
                                <span
                                    className="mh-sub-badge mh-sub-badge-link"
                                    title={`Open ${item.mixDj}'s set${item.mixTitle ? ` — ${item.mixTitle}` : ''}`}
                                    onClick={(e) => { e.stopPropagation(); goMix(dispatch, { id: item.mixId, publicId: item.mixPublicId, djName: item.mixDj, title: item.mixTitle }, 'browse'); }}
                                >
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>
                                    <span className="mh-sub-badge-name">{item.mixDj}</span>
                                </span>
                            )}
                        </div>
                    )
                ))}
            </div>
            )}

            {hasMore && !loading && (
                <div className="mh-browse-footer" ref={sentinelRef}>
                    {loadingMore && <span className="mix-play-spinner" />}
                </div>
            )}
        </div>
    );
};

export default MixpediaBrowsePage;
